import { type Ref, type RefCallback, useMemo } from 'react';

type MergeableRef<T> = Ref<T> | undefined;

const assignRef = <T>(
  ref: MergeableRef<T>,
  value: T | null
): ReturnType<RefCallback<T>> => {
  if (typeof ref === 'function') {
    return ref(value);
  }
  if (ref) {
    ref.current = value;
  }
};

const mergeRefs =
  <T>(refs: MergeableRef<T>[]): RefCallback<T> =>
  (value: T | null) => {
    const cleanups = refs.map(ref => assignRef(ref, value));
    return () =>
      cleanups.forEach((cleanup, index) => {
        if (typeof cleanup === 'function') {
          cleanup();
          return;
        }
        assignRef(refs[index], null);
      });
  };

/**
 * One callback ref that attaches an element to every given ref. On detach it
 * runs the cleanup a callback ref returned, or else resets that ref to `null`.
 * Keeps its identity while the given refs do.
 */
export const useMergedRefs = <T>(...refs: MergeableRef<T>[]): RefCallback<T> =>
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the refs are the deps
  useMemo(() => mergeRefs(refs), refs);
